import { useCallback, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { router } from '@/app/router'
import { useAgentEvents } from '@/shared/hooks/useAgentEvents'
import type { OrcaTaskCompletedEvent, PlanCompletedEvent } from '@/shared/data/system-integrations.types'
import { PlanCompletedModal } from './PlanCompletedModal'

/**
 * Mount một lần ở App root — lắng nghe tất cả agent events
 * và hiển thị notifications phù hợp.
 *
 * NOTE: Component này nằm NGOÀI <RouterProvider> (trong App.tsx) nên
 * KHÔNG được dùng useNavigate() — sẽ throw "useNavigate outside Router".
 * Dùng router.navigate() từ singleton thay thế.
 */
export function AgentEventsProvider({ children }: { children: React.ReactNode }) {
  const [completedPlan, setCompletedPlan] = useState<PlanCompletedEvent | null>(null)
  const queryClient = useQueryClient()

  const handleOrcaTaskCompleted = useCallback((event: OrcaTaskCompletedEvent) => {
    const isSuccess = event.status === 'completed'
    toast[isSuccess ? 'success' : 'error'](
      isSuccess ? 'Orca Task Completed' : 'Orca Task Failed',
      {
        description: `Task ${event.taskId.slice(0, 8)}... finished`,
        action: {
          label: 'View',
          onClick: () => router.navigate(`/workplace/orca-tasks/${event.taskId}`),
        },
      }
    )
    // Invalidate orca task queries
    queryClient.invalidateQueries({ queryKey: ['orca', 'tasks'] })
    queryClient.invalidateQueries({ queryKey: ['orca', 'task', event.taskId] })
  }, [queryClient])

  const handlePlanCompleted = useCallback((event: PlanCompletedEvent) => {
    if (event.status === 'completed') {
      setCompletedPlan(event)  // → open PlanCompletedModal
    } else {
      toast.error('Plan Generation Failed', {
        description: `Plan ${event.planId.slice(0, 8)}... failed with status: ${event.status}`,
      })
    }
    queryClient.invalidateQueries({ queryKey: ['planner', 'plans'] })
  }, [queryClient])

  useAgentEvents({
    onOrcaTaskCompleted: handleOrcaTaskCompleted,
    onPlanCompleted: handlePlanCompleted,
  })

  return (
    <>
      {children}
      {completedPlan && (
        <PlanCompletedModal
          planId={completedPlan.planId}
          onClose={() => setCompletedPlan(null)}
        />
      )}
    </>
  )
}
